import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IFieldInstance } from "../../../types/fieldInstance.interface";
import {
  convertToValidRSOption,
  formatRadioButtonOptions,
} from "../../../utils/formGenUtils/formatFieldOptionsMethods";
import {
  fieldTypes,
  getInitialValue,
} from "../../../utils/formGenUtils/getInitialValues";
import { getLookUpKey } from "../../../utils/formGenUtils/getLookUpKey";
import Autocomplete from "../autocomplete";
import CheckBoxGroup from "../checkboxGroup";
import CustomBPField from "../customBPField";
import CustomWoundComp from "../customWoundComp";
import {
  FieldContainer,
  HiddenField,
} from "../incidentForm/incidentForm.style";
import FormInput from "../input/FormInput";
import RLFormComp from "../rlFormComp";
import TextAreaInput from "../textArea";
import UnitLabelledInput from "../unitLabelledInput";
import DateTimeInput from "../dateTimeInput";
import { getPickListValues } from "./methods";
import { useIncidentFormContext } from "../incidentForm/IncidentContext";
import { FormikValues, useFormikContext } from "formik";
import { findFormfieldById } from "../../../utils/formGenUtils/hashTable";
import { getFieldMaxLength } from "../../../utils/formGenUtils/fieldMaxLengths";
import { shouldFieldRender } from "../../../utils/formGenUtils/viewModelMethods";
import { useSectionContext } from "../context/SectionContext";
import {
  PARENT_TYPES_WITH_INLINE_CONTROLS,
  INLINE_FIELD_TYPES,
} from "../../../utils/formGenUtils/fieldTypeConstants";
import { ESTIMATED_DATE_TIME_LABEL } from "../../EstimatedDateTime";

interface Iprops {
  instance: IFieldInstance;
  getError: any;
  typeControlName: string | undefined;
  showToggleBox: boolean | undefined;
  typeControlLabel: string | undefined;
  inputName: string | undefined;
  secondaryBPFieldName: string | undefined;
  checkBoxFieldName: string | undefined;
  checkBoxLabel: string | undefined;
  disabled: boolean;
  qaMode?: boolean;
  tooltipContent?: string;
  rlParentFieldName?: string;
  childFormGroup?: {
    [id: string]: any;
  };
  allWoundFields?: IFieldInstance[];
}

// capture placeholder info from sheet
export const FormField: React.FC<Iprops> = ({
  instance,
  getError,
  showToggleBox,
  typeControlName,
  typeControlLabel,
  inputName,
  secondaryBPFieldName,
  checkBoxFieldName,
  checkBoxLabel,
  disabled,
  qaMode,
  tooltipContent,
  rlParentFieldName,
  childFormGroup,
  allWoundFields,
}) => {
  const {
    rlViewModel,
    setDeleteRLRecordObject,
    selectOptions,
    setShowConfirmationPrompt,
    setTempValue,
    shouldResetForm,
  } = useIncidentFormContext();
  
  // Access section context to check if the section is disabled
  const { isSectionDisabled } = useSectionContext();
  
  // Get the section name from the instance object
  const sectionName = instance.section;

  // MARCC-799: formRLs.json reuses form.json's small integer ids, so a related-list
  // field's parent/child ids must be resolved in its own RL scope. Unscoped lookups
  // silently return the form.json field with that id (skinIssues/injuries Area__c
  // owns "7" and "8", which are Patient Demographics caregiver fields).
  const resolveFieldById = useCallback(
    (id: string) => findFormfieldById(id, rlParentFieldName),
    [rlParentFieldName]
  );

  // Check if this field's parent is a section-level toggle
  // A section-level toggle is a Togglebox without a parent of its own
  const isChildOfSectionLevelToggle = !!instance.parentFieldId && (() => {
    try {
      // Find this field's parent
      const parentField = resolveFieldById(instance.parentFieldId);
      // If parent exists, is a Togglebox, and has no parent itself, it's a section-level toggle
      return parentField && 
             parentField.fieldType === 'Togglebox' && 
             !parentField.parentFieldId;
    } catch (error) {
      console.error('Error checking parent toggle:', error);
      return false;
    }
  })();
  

  // If the field is a child of a section-level toggle, exempt it from section disabling
  const sectionDisabled = isChildOfSectionLevelToggle ? false : isSectionDisabled(sectionName);
  
  // The final disabled state combines the prop and the section state
  const fieldDisabled = disabled || sectionDisabled;
  
  


  const [shouldAskConfirmation, setShouldAskConfirmation] = useState(false);
  const [depField, setDepField] = useState("");
  let formElement: JSX.Element;
  const {
    fieldLabel,
    fieldName,
    fieldType,
    unit,
    values: options,
    required: isRequired,
    qapi,
    constraint,
  } = instance;
  const {
    handleChange,
    handleBlur,
    values,
    setFieldValue,
    setFieldTouched,
    setFieldError,
    getFieldProps,
  } = useFormikContext();

  const formValues = values as FormikValues;

  const { allLookUpOptions, allPickListOptions, rlPickListOptions } =
    selectOptions;
  const lookUpKey = options[0]?.includes("useLookup")
    ? getLookUpKey(options.join(","))
    : "";

  const lookupValues = lookUpKey ? allLookUpOptions[lookUpKey] : [];

  const pickListValues = useMemo(
    () =>
      getPickListValues(
        fieldName,
        formValues,
        options,
        rlParentFieldName,
        allPickListOptions,
        rlPickListOptions 
      ),
    [allPickListOptions]
  );

  let hasHiddenField = false;

  // Cap free-text inputs at the backing Salesforce field length so an
  // over-length value can't be submitted and fail at the Apex upsert
  // (STRING_TOO_LONG, MARCC-690). undefined for fields with no known length.
  const fieldMaxLength = getFieldMaxLength(fieldName);

  const fieldValue = !(fieldName in formValues)
    ? _.get(formValues, fieldName)
    : formValues[fieldName];

  function getSecondaryFieldValue(fieldName: string | undefined) {
    if (!fieldName) return;
    return !(fieldName in formValues) && fieldName.includes(".")
      ? _.get(formValues, fieldName)
      : formValues[fieldName];
  }

  useEffect(() => {
    // if child is RL and contains values, set getConfirmation to true
    // if getConfirmation is true, set shouldConfirmationPrompt to true
    // if shouldConfirmationPrompt is true, save value in state, then set when prompt is checked...
    // Given the child Id, how can we get the child fieldName? Check hashMap?
    if (instance.askConfirmation) {
      const childField = resolveFieldById(instance.childFieldIds[0]);
      const childFieldValue = childField
        ? formValues[childField.fieldName]
        : undefined;
      setShouldAskConfirmation(
        !!(Array.isArray(childFieldValue) && childFieldValue.length)
      );
      setDepField(childField?.fieldName ?? "");
    }
  }, [formValues]);

  // Track previous visibility state for child fields to prevent unnecessary resets
  const childVisibilityRef = useRef<Record<string, boolean>>({});

  /**
   * Collects the names of all parent fields that influence child field visibility.
   * This includes the current field and all ancestor fields in the parent chain.
   * Memoized to avoid recalculating on every render - only changes when child structure changes.
   */
  const relevantFieldNames = useMemo(() => {
    if (!instance.childFieldIds?.length) return [];

    const collected = new Set<string>();
    
    const collectInfluencingFields = (childIds: string[]) => {
      childIds.forEach((childId) => {
        const childField = resolveFieldById(childId);
        if (!childField) return;

        // Walk up the parent chain to collect all ancestor field names
        let currentField = childField;
        while (currentField.parentFieldId) {
          const parentField = resolveFieldById(currentField.parentFieldId);
          if (!parentField) break;
          collected.add(parentField.fieldName);
          currentField = parentField;
        }

        // Recursively process nested children
        if (childField.childFieldIds?.length) {
          collectInfluencingFields(childField.childFieldIds);
        }
      });
    };

    collectInfluencingFields(instance.childFieldIds);
    
    // Include the current field as it may also influence visibility
    collected.add(instance.fieldName);

    return Array.from(collected).sort();
  }, [instance.childFieldIds, instance.fieldName, resolveFieldById]);

  /**
   * Creates a stringified signature of influencing field values.
   * When this signature changes, it indicates that a field affecting child visibility has changed.
   * This acts as a narrow dependency for the child-reset effect, preventing unnecessary runs.
   */
  const influencingFieldSignature = useMemo(() => {
    if (!relevantFieldNames.length) return "";

    const snapshot = relevantFieldNames.reduce<Record<string, unknown>>(
      (acc, name) => {
        acc[name] = _.get(formValues, name);
        return acc;
      },
      {}
    );

    return JSON.stringify(snapshot);
  }, [relevantFieldNames, formValues]);

  /**
   * Manages child field visibility and resets values when fields become hidden.
   * This effect only runs when influencing parent field values change, not on every form update.
   * 
   * Key behaviors:
   * - Recursively evaluates visibility for all child fields using shouldFieldRender
   * - Resets values only when a field transitions from visible to hidden
   * - Preserves values for fields that remain visible or were already hidden
   * - Skips resetting inline control fields (Togglebox children of Lookup/Picklist, and their Text children)
   * - Cleans up field state (value, touched, error) on reset
   *
   * Does not run for related-list fields: their child ids live in the RL's own id
   * space and their values are namespaced (`Skin_Issues__c[0].Area__c`), so this
   * effect can only resolve/write the wrong keys. RL child visibility is owned by
   * `recordViewModel` and cleanup by `shouldCleanupField` in FieldArrayFields.
   */
  useEffect(() => {
    if (rlParentFieldName) return;
    if (!instance.childFieldIds?.length) return;

    const nextVisibility: Record<string, boolean> = {};
     
    const resetChildValues = (childIds: string[], parentFieldType?: string) => {
      childIds.forEach((childId) => {
        const childField = resolveFieldById(childId);
        if (!childField) return;

        // Skip resetting Togglebox fields that are children of fields with inline toggles
        // These are rendered as checkboxes within the parent component (e.g., Autocomplete, CustomBPField)
        const isInlineTogglebox = PARENT_TYPES_WITH_INLINE_CONTROLS.includes(parentFieldType as any) && 
                                   childField.fieldType === "Togglebox";
        
        // Skip resetting Text fields that are children of Togglebox
        // These render inline within Autocomplete/FormInput components.
        // Deliberately excludes TextArea: shouldRenderIfBoolean hard-codes false only
        // for Text, so a TextArea child (an N/A rationale) is a real standalone field
        // that must still be cleared when its toggle goes off.
        const isInlineTextField = parentFieldType === "Togglebox" && childField.fieldType === "Text";

        // Skip resetting Checkbox fields that are children of fields with inline toggles
        // These are managed internally by their parent components (e.g., CustomBPField's Orthostatic checkbox)
        const isInlineCheckbox = PARENT_TYPES_WITH_INLINE_CONTROLS.includes(parentFieldType as any) && 
                                  childField.fieldType === "Checkbox";

        // Skip resetting SecondaryBPField - it's always managed by CustomBPField
        const isSecondaryBPField = childField.fieldType === "SecondaryBPField";

        const shouldRenderChild = shouldFieldRender(
          childField,
          formValues,
          resolveFieldById
        );

        nextVisibility[childId] = shouldRenderChild;
        const wasVisible = childVisibilityRef.current[childId];

        // Reset only on visibility transition from visible → hidden
        // Skip inline controls as they're managed by their parent components
        if (!shouldRenderChild && wasVisible !== false && !isInlineTogglebox && !isInlineTextField && !isSecondaryBPField && !isInlineCheckbox) {
          const childInitialValue = getInitialValue(
            childField.fieldType as keyof typeof fieldTypes
          );
          const childCurrentValue = _.get(formValues, childField.fieldName);

          if (!_.isEqual(childCurrentValue, childInitialValue)) {
            setFieldValue(childField.fieldName, childInitialValue);
            setFieldTouched(childField.fieldName, false);
            setFieldError(childField.fieldName, undefined);
          }
        }

        // Recursively process nested children, passing current field type as parent
        if (childField.childFieldIds?.length) {
          resetChildValues(childField.childFieldIds, childField.fieldType);
        }
      });
    };

    resetChildValues(instance.childFieldIds, instance.fieldType);
    childVisibilityRef.current = nextVisibility;
  }, [
    influencingFieldSignature,
    instance.childFieldIds,
    rlParentFieldName,
    resolveFieldById,
    setFieldValue,
    setFieldTouched,
    setFieldError,
  ]);

  useEffect(() => {
    if (!shouldResetForm) return;
    const initialValue = getInitialValue(
      fieldType as keyof typeof fieldTypes
    );
    const shouldCleanupField = fieldName in formValues;
    if (shouldCleanupField) {
      setFieldValue(fieldName, initialValue);
    }
  }, [shouldResetForm]);

  switch (fieldType) {
    case "Text":
      formElement = (
        <FormInput
          // data-testid="text-input"
          data-testid={`text-input-${fieldName}`}
          label={fieldLabel}
          name={fieldName}
          placeholder={fieldLabel}
          variant="sm"
          onChange={handleChange}
          setFieldValue={setFieldValue}
          value={fieldValue}
          onBlur={handleBlur}
          errorMessage={getError(fieldName)}
          isImportant={isRequired}
          disabled={fieldDisabled}
          showBadges={qaMode}
          qapi={qapi}
          maxLength={fieldMaxLength}
        />
      );
      break;
    case "TextArea":
      formElement = (
        <TextAreaInput
          data-testid={`textarea-input-${fieldName}`}
          label={fieldLabel}
          name={fieldName}
          placeholder={fieldLabel}
          variant="sm"
          onChange={handleChange}
          setFieldValue={setFieldValue}
          value={fieldValue}
          onBlur={handleBlur}
          errorMessage={getError(fieldName)}
          isImportant={isRequired}
          disabled={fieldDisabled}
          showBadges={qaMode}
          qapi={qapi}
          maxLength={fieldMaxLength}
        />
      );
      break;
    case "TextWithLabel":
      formElement = (
        <UnitLabelledInput
          data-testid={`text-with-label-input-${fieldName}`}
          label={fieldLabel}
          name={fieldName}
          placeholder={fieldLabel}
          variant="sm"
          value={fieldValue}
          unitLabelOptions={pickListValues}
          onBlur={handleBlur}
          errorMessage={getError(fieldName, true)}
          unitLabel={unit}
          showToggleBox={showToggleBox}
          toggleBoxName={typeControlName}
          toggleBoxLabel={typeControlLabel}
          toggleBoxValue={getSecondaryFieldValue(typeControlName)}
          isImportant={isRequired}
          setFieldValue={setFieldValue}
          disabled={fieldDisabled}
          showBadges={qaMode}
          qapi={qapi}
        />
      );
      break;
    case "Number / Currency":
      formElement = (
        <FormInput
          data-testid={`number-currency-input-${fieldName}`}
          label={fieldLabel}
          name={fieldName}
          placeholder={fieldLabel}
          variant="sm"
          onChange={handleChange}
          value={fieldValue}
          onBlur={handleBlur}
          errorMessage={getError(fieldName, true)}
          isImportant={isRequired}
          disabled={fieldDisabled}
          showBadges={qaMode}
          qapi={qapi}
        />
      );
      break;
    case "Date/Time": {
      // toggle on flips the label to the estimated wording (picker stays enabled).
      const isEstimatedDateTime = !!getSecondaryFieldValue(typeControlName);
      const effectiveLabel = isEstimatedDateTime ? ESTIMATED_DATE_TIME_LABEL : fieldLabel;
      formElement = (
        <DateTimeInput
          data-testid={`datetime-input-${fieldName}`}
          label={effectiveLabel}
          name={fieldName}
          placeholder={effectiveLabel}
          variant="sm"
          handleChange={setFieldValue}
          value={fieldValue}
          onBlur={handleBlur}
          errorMessage={getError(fieldName)}
          isImportant={isRequired}
          disabled={fieldDisabled}
          showToggleBox={showToggleBox}
          toggleBoxName={typeControlName}
          toggleBoxLabel={typeControlLabel}
          toggleBoxValue={isEstimatedDateTime}
          tooltipContent={
            "Double click or click calendar icon to open date time selector"
          }
          showBadges={qaMode}
          qapi={qapi}
          constraint={constraint}
          incidentDateTime={formValues["Incident_Date_Time__c"]}
          incidentCreationDate={formValues["CreatedDate"] ?? ""}
        />
      );
      break;
    }
    case "Checkbox":
    case "SecondaryBPField":
      hasHiddenField = true;
      formElement = <HiddenField data-testid={`hidden-field-${fieldName}`} />;
      break;
    case "Picklist":
      formElement = (
        <Autocomplete
          data-testid={`picklist-autocomplete-${fieldName}`}
          label={fieldLabel}
          name={fieldName}
          placeholder={fieldLabel}
          options={convertToValidRSOption(pickListValues)}
          setFieldValue={setFieldValue}
          value={fieldValue}
          handleBlur={handleBlur}
          errorMessage={getError(fieldName)}
          showToggleBox={showToggleBox}
          toggleBoxName={typeControlName}
          toggleBoxLabel={typeControlLabel}
          inputName={inputName}
          inputValue={getSecondaryFieldValue(inputName)}
          isImportant={isRequired}
          toggleBoxValue={getSecondaryFieldValue(typeControlName)}
          isDisabled={fieldDisabled}
          showBadges={qaMode}
          qapi={qapi}
        />
      );
      break;
    case "Lookup":
      formElement = (
        <Autocomplete
          data-testid={`lookup-picklist-${fieldName}`}
          label={fieldLabel}
          name={fieldName}
          placeholder={fieldLabel}
          options={lookupValues}
          setFieldValue={setFieldValue}
          value={fieldValue}
          handleBlur={handleBlur}
          errorMessage={getError(fieldName)}
          isSearch
          showToggleBox={showToggleBox}
          toggleBoxName={typeControlName}
          toggleBoxLabel={typeControlLabel}
          inputName={inputName}
          inputValue={getSecondaryFieldValue(inputName)}
          isImportant={isRequired}
          toggleBoxValue={getSecondaryFieldValue(typeControlName)}
          isDisabled={fieldDisabled}
          showBadges={qaMode}
          qapi={qapi}
        />
      );
      break;
    case "Multi-Select Picklist":
      formElement = (
        <Autocomplete
          data-testid={`multi-select-picklist-${fieldName}`}
          label={fieldLabel}
          name={fieldName}
          placeholder={fieldLabel}
          options={convertToValidRSOption(pickListValues)}
          setFieldValue={setFieldValue}
          value={fieldValue}
          handleBlur={handleBlur}
          errorMessage={getError(fieldName)}
          isMulti
          isImportant={isRequired}
          isDisabled={fieldDisabled}
          showBadges={qaMode}
          qapi={qapi}
          showToggleBox={showToggleBox}
          toggleBoxName={typeControlName}
          toggleBoxLabel={typeControlLabel}
          inputName={inputName}
          inputValue={getSecondaryFieldValue(inputName)}
          toggleBoxValue={getSecondaryFieldValue(typeControlName)}
        />
      );
      break;
    case "Multi-Select as CheckBoxes":
      formElement = (
        <CheckBoxGroup
          options={formatRadioButtonOptions(instance, pickListValues)}
          groupName={fieldName}
          label={fieldLabel}
          errorMessage={getError(fieldName)}
          value={fieldValue}
          handleChange={getFieldProps}
          isImportant={isRequired}
          onBlur={handleBlur}
          disabled={fieldDisabled}
          showBadges={qaMode}
          qapi={qapi}
        />
      );
      break;
    case "Branched Multi-Select as CheckBoxes":
      formElement = (
        <CheckBoxGroup
          data-testid={`branched-multi-select-checkboxes-${fieldName}`}
          options={formatRadioButtonOptions(instance, pickListValues)}
          groupName={fieldName}
          label={fieldLabel}
          errorMessage={getError(fieldName)}
          value={fieldValue}
          handleChange={getFieldProps}
          isImportant={isRequired}
          onBlur={handleBlur}
          disabled={fieldDisabled}
          showBadges={qaMode}
          qapi={qapi}
          childFields={childFormGroup}
          getError={getError}
        />
      );
      break;
    case "Radio Buttons":
      formElement = (
        <CheckBoxGroup
          data-testid={`radio-buttons-${fieldName}`}
          options={formatRadioButtonOptions(instance, pickListValues)}
          groupName={fieldName}
          label={fieldLabel}
          errorMessage={getError(fieldName)}
          value={fieldValue}
          handleChange={getFieldProps}
          isImportant={isRequired}
          isExclusive
          onBlur={handleBlur}
          disabled={fieldDisabled}
          showBadges={qaMode}
          qapi={qapi}
          shouldAskConfirmation={shouldAskConfirmation}
          setTempValue={setTempValue}
          setShowConfirmationPrompt={setShowConfirmationPrompt}
          depField={depField}
        />
      );
      break;
    case "CustomBPField":
      formElement = (
        <CustomBPField
          data-testid={`custom-bp-field-${fieldName}`}
          label={fieldLabel as string}
          placeholder={fieldLabel}
          onBlur={handleBlur}
          unitLabel={unit}
          setFieldValue={setFieldValue}
          toggleBoxLabel={typeControlLabel}
          isImportant={isRequired}
          firstFieldName={fieldName}
          onChange={handleChange}
          secondFieldName={secondaryBPFieldName as string}
          checkBoxFieldName={checkBoxFieldName as string}
          firstFieldValue={fieldValue}
          secondFieldValue={getSecondaryFieldValue(secondaryBPFieldName)}
          checkBoxValue={getSecondaryFieldValue(checkBoxFieldName)}
          firstFieldErrorMessage={getError(fieldName)}
          checkBoxLabel={checkBoxLabel as string}
          showToggleBox={showToggleBox}
          toggleBoxName={typeControlName}
          toggleBoxValue={getSecondaryFieldValue(typeControlName)}
          disabled={fieldDisabled}
          showBadges={qaMode}
          qapi={qapi}
        />
      );
      break;
    case "RelatedList":
      formElement = (
        <RLFormComp
          data-testid={`related-list-${fieldName}`}
          fieldName={fieldName}
          fieldLabel={fieldLabel as string}
          getError={getError}
          value={fieldValue}
          selectOptions={selectOptions}
          isRequired={isRequired}
          rlViewModel={rlViewModel}
          setDeleteRLRecordObject={setDeleteRLRecordObject}
          disabled={fieldDisabled}
          showBadges={!!qaMode}
          qapi={qapi}
        />
      );
      break;
    case "CustomWoundComp":
      formElement = (
        <CustomWoundComp
          data-testid={`custom-wound-comp-${fieldName}`}
          getErrorMessage={getError}
          values={formValues}
          onChange={handleChange}
          isImportant={isRequired}
          onBlur={handleBlur}
          disabled={fieldDisabled}
          showBadges={qaMode}
          qapi={qapi}
          fieldName={fieldName}
          label={fieldLabel}
          allWoundFields={allWoundFields ?? []}
        />
      );
      break;

    default:
      throw new Error(`Invalid field type: ${fieldType}`);
  }

  return (
    <FieldContainer hasHiddenField={hasHiddenField}>
      {formElement}
    </FieldContainer>
  );
};
